import { DateTime } from 'luxon'
import { BadgeCardProps } from '@pandiafi/components'

import type { CoreParkingSpot } from '../../../common/validations/contractParkingSpotsSchema'
import {
  ParkingSpotEquipment,
  ParkingSpotEquipments,
} from '../../../common/types'

/**
 * Returns today's date as YYYY-MM-DD (safe for lexicographic comparison).
 */
export const todayISO = (): string =>
  DateTime.now().toISODate() ?? new Date().toISOString().slice(0, 10)

/**
 * Treats undefined, empty string, and literal "(null)" as nullish.
 */
export const isNullishStr = (v: string | null): boolean => !v || v === '(null)'

/**
 * Normalize API date to YYYY-MM-DD or undefined.
 */
export const normDate = (v: string | null): string | undefined =>
  isNullishStr(v) ? undefined : String(v).slice(0, 10)

/**
 * Format a number as a localized currency string.
 * Uses 0 or 2 fraction digits depending on decimals.
 */
export const fmtCurrency = (
  amount: number,
  locale: string,
  currency: string = 'EUR'
): string =>
  new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    maximumFractionDigits: amount % 1 ? 2 : 0,
    minimumFractionDigits: amount % 1 ? 2 : 0,
  }).format(amount)

/** API price period. */
export type PricePeriod = {
  paymentTypeId: number
  nameOnBill: string
  nameOnBillSv: string | null
  nameOnBillEn: string | null
  price: number
  validFrom?: string | null
  validTo?: string | null
}

const isActiveOn = (period: PricePeriod, dateISO: string): boolean => {
  const fromISO = normDate(period.validFrom ?? null) ?? '0000-01-01'
  const toISO = normDate(period.validTo ?? null) ?? '9999-12-31'
  return fromISO <= dateISO && dateISO <= toISO
}

/**
 * A parking spot can be priced with several payment types (e.g. spot rent + electricity).
 * Picks the price valid on the given date for each payment type, preferring the most
 * recent validFrom. Result keeps the payment type order given by the API.
 */
export const pricesForDate = (
  prices: PricePeriod[] | undefined,
  atISO: string | null
): PricePeriod[] => {
  if (!prices?.length) return []
  const at = atISO ? atISO.slice(0, 10) : todayISO()

  const activeByPaymentType = new Map<number, PricePeriod>()
  for (const period of prices) {
    if (!isActiveOn(period, at)) continue

    const chosen = activeByPaymentType.get(period.paymentTypeId)
    const chosenFrom = normDate(chosen?.validFrom ?? null) ?? '0000-01-01'
    const periodFrom = normDate(period.validFrom ?? null) ?? '0000-01-01'
    if (!chosen || periodFrom > chosenFrom) {
      activeByPaymentType.set(period.paymentTypeId, period)
    }
  }

  return [...activeByPaymentType.values()]
}

export const totalPrice = (prices: PricePeriod[]): number =>
  Math.round(prices.reduce((sum, period) => sum + period.price, 0) * 100) / 100

/**
 * Total price valid today (inclusive) across all payment types.
 */
export const priceForToday = (
  prices?: PricePeriod[],
  today: string = todayISO()
): number | undefined => priceForDate(prices, today)

/**
 * Total price valid for an arbitrary date (inclusive) across all payment types.
 */
export const priceForDate = (
  prices: PricePeriod[] | undefined,
  atISO: string | null
): number | undefined => {
  const active = pricesForDate(prices, atISO)
  return active.length === 0 ? undefined : totalPrice(active)
}

// ---------------------------------------------------------------------
// Date range helpers
// ---------------------------------------------------------------------

/**
 * Returns true if the given day falls between start and end dates.
 */
const isDateInRange = (
  date: string,
  start: string | null,
  end: string | null
): boolean => {
  const d = date
  const s = start ?? '0000-01-01'
  const e = end ?? '9999-12-31'

  return d >= s && d <= e
}

// ---------------------------------------------------------------------
// Reservation checks
// ---------------------------------------------------------------------

/**
 * Does the user currently have an OWN reservation?
 */
export const hasOwnNow = (
  spot: CoreParkingSpot,
  today: string = todayISO()
): boolean =>
  (spot.reservations ?? []).some((r) => {
    return (
      r.kind === 'own' &&
      (!normDate(r.startDate) || normDate(r.startDate)! <= today) &&
      (!normDate(r.endDate) || normDate(r.endDate)! >= today)
    )
  })

/**
 * Does the user have a future OWN reservation (strictly after today)?
 */
export const hasOwnFuture = (
  spot: CoreParkingSpot,
  today: string = todayISO()
): boolean =>
  (spot.reservations ?? []).some((r) => {
    if (r.kind !== 'own') return false
    const start = normDate(r.startDate)
    const end = normDate(r.endDate)
    if (!start) return false
    return start > today && (!end || end >= start)
  })

/**
 * Does the user have any own reservation for this spot (current or future)?
 */
export const hasOwnReservation = (
  spot: CoreParkingSpot,
  today: string = todayISO()
): boolean => hasOwnNow(spot, today) || hasOwnFuture(spot, today)

/**
 * Does another resident have a future reservation for this spot (strictly after today)?
 */
export const hasOtherFuture = (
  spot: CoreParkingSpot,
  today: string = todayISO()
): boolean =>
  (spot.reservations ?? []).some((r) => {
    if (r.kind === 'own') return false
    const start = normDate(r.startDate)
    return !!start && start > today
  })

// ---------------------------------------------------------------------
// Availability
// ---------------------------------------------------------------------

/**
 * Returns true if the parking spot is free for the entire current day.
 * Checks availabilityRanges first (if any), otherwise checks reservations.
 */
export const isFreeToday = (
  spot: Pick<CoreParkingSpot, 'reservations' | 'availableRanges'>
): boolean => {
  const today = todayISO()

  if (spot.availableRanges?.length) {
    return spot.availableRanges.some((r) =>
      isDateInRange(today, r.startDate, r.endDate)
    )
  }

  return !(spot.reservations ?? []).some((r) =>
    isDateInRange(today, r.startDate, r.endDate)
  )
}

export type QueueItem = {
  equipmentWishes: ParkingSpotEquipment[]
}

type QueueEquipmentGroup = 'electricity' | 'weather' | 'type'

export const QUEUE_EQUIPMENT_DISPLAY_ORDER: ReadonlyArray<ParkingSpotEquipment> =
  [
    'heating_outlet',
    'ev_charger',
    'building',
    'covered',
    'garage',
    'yard',
    'motorbike_spot',
    'disabled_spot',
  ]

const QUEUE_EQUIPMENT_GROUPS: Record<
  QueueEquipmentGroup,
  ReadonlyArray<ParkingSpotEquipment>
> = {
  electricity: ['heating_outlet', 'ev_charger'],
  weather: ['building', 'covered', 'garage', 'yard'],
  type: ['motorbike_spot', 'disabled_spot'],
}

const QUEUE_EQUIPMENT_GROUP_BY_VALUE: Record<
  ParkingSpotEquipment,
  QueueEquipmentGroup
> = {
  heating_outlet: 'electricity',
  ev_charger: 'electricity',
  building: 'weather',
  covered: 'weather',
  garage: 'weather',
  yard: 'weather',
  motorbike_spot: 'type',
  disabled_spot: 'type',
}

const isParkingSpotEquipment = (
  value: string | null | undefined
): value is ParkingSpotEquipment =>
  typeof value === 'string' &&
  ParkingSpotEquipments.includes(value as ParkingSpotEquipment)

const orderEquipment = (
  equipment: ReadonlyArray<ParkingSpotEquipment>
): ParkingSpotEquipment[] =>
  QUEUE_EQUIPMENT_DISPLAY_ORDER.filter((value) => equipment.includes(value))

const normalizeEquipment = (
  equipment: ReadonlyArray<string | ParkingSpotEquipment> | null | undefined
): ParkingSpotEquipment[] => {
  const unique = Array.from(
    new Set((equipment ?? []).filter(isParkingSpotEquipment))
  )

  return orderEquipment(unique)
}

type GroupedQueueEquipment = Record<QueueEquipmentGroup, ParkingSpotEquipment[]>

const groupQueueEquipment = (
  equipment: ReadonlyArray<string | ParkingSpotEquipment>
): GroupedQueueEquipment =>
  normalizeEquipment(equipment).reduce<GroupedQueueEquipment>(
    (acc, value) => {
      acc[QUEUE_EQUIPMENT_GROUP_BY_VALUE[value]].push(value)
      return acc
    },
    {
      electricity: [],
      weather: [],
      type: [],
    }
  )

const spotHasEquipmentInGroup = (
  spotEquipment: ReadonlyArray<ParkingSpotEquipment>,
  group: QueueEquipmentGroup
): boolean =>
  QUEUE_EQUIPMENT_GROUPS[group].some((value) => spotEquipment.includes(value))

const spotMatchesGroupedQueueEquipment = (
  spotEquipment: ReadonlyArray<ParkingSpotEquipment>,
  groupedWishes: GroupedQueueEquipment
): boolean => {
  const { electricity, weather, type } = groupedWishes

  if (
    electricity.length > 0 &&
    !electricity.some((wish) => spotEquipment.includes(wish))
  ) {
    return false
  }

  if (
    electricity.length === 0 &&
    (weather.length > 0 || type.length > 0) &&
    spotHasEquipmentInGroup(spotEquipment, 'electricity')
  ) {
    return false
  }

  if (
    weather.length > 0 &&
    !weather.some((wish) => spotEquipment.includes(wish))
  ) {
    return false
  }

  if (type.length > 0 && !type.some((wish) => spotEquipment.includes(wish))) {
    return false
  }

  if (
    type.length === 0 &&
    QUEUE_EQUIPMENT_GROUPS.type.some((value) => spotEquipment.includes(value))
  ) {
    return false
  }

  return true
}

export const spotMatchesEquipmentWishes = (
  spotEquipment: ReadonlyArray<string | ParkingSpotEquipment>,
  equipmentWishes: ReadonlyArray<ParkingSpotEquipment>
): boolean => {
  if (!equipmentWishes || equipmentWishes.length === 0) {
    return true
  }

  return spotMatchesGroupedQueueEquipment(
    normalizeEquipment(spotEquipment),
    groupQueueEquipment(equipmentWishes)
  )
}

export const isReservedByQueue = (
  spot: CoreParkingSpot,
  queueItems: ReadonlyArray<QueueItem>
): boolean => {
  if (!queueItems || queueItems.length === 0) {
    return false
  }
  const spotEquipment = spot.equipment ?? []
  return queueItems.some((item) =>
    spotMatchesEquipmentWishes(spotEquipment, item.equipmentWishes)
  )
}

export const getExistingQueueEquipmentTypes = (
  spots: ReadonlyArray<CoreParkingSpot>
): ParkingSpotEquipment[] =>
  orderEquipment(
    Array.from(
      new Set(spots.flatMap((spot) => normalizeEquipment(spot.equipment)))
    )
  )

const buildEquipmentSubsets = (
  equipment: ReadonlyArray<ParkingSpotEquipment>
): ParkingSpotEquipment[][] => {
  const subsets: ParkingSpotEquipment[][] = []
  const count = 1 << equipment.length

  for (let mask = 1; mask < count; mask += 1) {
    const subset = equipment.filter((_, index) => (mask & (1 << index)) !== 0)
    subsets.push(subset)
  }

  return subsets
}

export const isJoinableQueueSelection = (
  spots: ReadonlyArray<CoreParkingSpot>,
  queueItems: ReadonlyArray<QueueItem> = [],
  equipmentWishes: ReadonlyArray<ParkingSpotEquipment>
): boolean => {
  if (equipmentWishes.length === 0) {
    return false
  }

  const matchingSpots = spots.filter((spot) =>
    spotMatchesEquipmentWishes(spot.equipment ?? [], equipmentWishes)
  )

  if (matchingSpots.length === 0) {
    return false
  }

  return !matchingSpots.some(
    (spot) => mapStatus(spot, queueItems) === 'default'
  )
}

export const getJoinableQueueEquipmentTypes = (
  spots: ReadonlyArray<CoreParkingSpot>,
  queueItems: ReadonlyArray<QueueItem> = [],
  selectedEquipment: ReadonlyArray<ParkingSpotEquipment> = []
): ParkingSpotEquipment[] => {
  const existingEquipments = getExistingQueueEquipmentTypes(spots)
  const selected = normalizeEquipment(selectedEquipment)
  const joinableSelections = buildEquipmentSubsets(existingEquipments).filter(
    (equipmentWishes) =>
      isJoinableQueueSelection(spots, queueItems, equipmentWishes)
  )

  return existingEquipments.filter((equipment) => {
    if (selected.includes(equipment)) {
      return true
    }

    return joinableSelections.some(
      (selection) =>
        selection.includes(equipment) &&
        selected.every((selectedItem) => selection.includes(selectedItem))
    )
  })
}

export const hasJoinableQueueSelectionRefinement = (
  spots: ReadonlyArray<CoreParkingSpot>,
  queueItems: ReadonlyArray<QueueItem> = [],
  selectedEquipment: ReadonlyArray<ParkingSpotEquipment> = []
): boolean => {
  const selected = normalizeEquipment(selectedEquipment)

  if (selected.length === 0) {
    return false
  }

  return getJoinableQueueEquipmentTypes(spots, queueItems, selected).some(
    (equipment) => !selected.includes(equipment)
  )
}

// ---------------------------------------------------------------------
// Status mapping
// ---------------------------------------------------------------------

/**
 * Map CoreParkingSpot to a UI status.
 *
 * Priority:
 * 1. active     — ongoing OWN reservation
 * 2. upcoming   — future OWN reservation
 * 3. not-in-use — explicitly unused
 * 4. disabled   — reserved by queue, has another resident's future reservation, or otherwise busy
 * 5. default    — free on the selected range
 */
export const mapStatus = (
  spot: CoreParkingSpot,
  queueItems?: ReadonlyArray<QueueItem>,
  today: string = todayISO()
): BadgeCardProps['status'] => {
  if (hasOwnNow(spot, today)) return 'active'
  if (hasOwnFuture(spot, today)) return 'upcoming'
  if (!spot.isInUse) return 'not-in-use'

  if (queueItems && isReservedByQueue(spot, queueItems)) {
    return 'disabled'
  }

  if (hasOtherFuture(spot, today)) return 'disabled'

  return isFreeToday(spot) ? 'default' : 'disabled'
}
